import { Link } from '../../../../widgets/buttons';
import { PageHeader } from '../../../../widgets/pageHeader';
import { Section } from '../../../../widgets/section';
import { SelectionType, SelectionTypeContainer } from '../../../../widgets/selectionType';
import { ContainerBase } from '../../../../widgets/layouts';
import React, { FunctionComponent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuickGuidesSection } from 'src/components/widgets/section';
import { Guides } from 'src/shared/globals';
import { backofficeUrl, T } from 'src/shared/helpers';
import EmptyProductImage from 'src/shared/icons/image.svg';
import styled from 'styled-components';
import composerIcon from '../../../shared/images/composer_selection_thumb.png';
import sceneEditorIcon from '../../../shared/images/scene_editor_selection_thumb.png';
import shoppingPreviewIcon from '../../../shared/images/shopping_preview_selection_thumb.png';
import { modeAction, setAlreadyDoneRedirectToSceneEditor, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';

const Container = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-left: 35vh;
    padding-top: 5vh;
    background-color: rgb(246, 248, 249);
    z-index: 10;
`;

const ProductImage = styled.img`
    width: 100px;
    height: 100px;
    margin-bottom: 20px;
`;

const StepIcon = styled.img`
    height: 120px;
    margin-bottom: 20px;
`;

const StyledEmptyProductImage = styled(EmptyProductImage)`
    width: 100px;
    height: 100px;
`;

const PCStepSelection: FunctionComponent<{}> = () => {
    const { dispatch, state } = useProductConfigurationState();
    const navigate = useNavigate();
    const product = state.product;
    const changeStep = useProductConfigurationChangeStep();
    const configuratorSteps = [
        {
            key: 'Scene Editor',
            tip: (
                <>
                    The 3D Scene Editor section is where you upload and manage the 3D models of your products that you have developed according to our
                    guidelines.
                </>
            ),
            label: 'Scene Editor',
            icon: <StepIcon src={sceneEditorIcon} alt='Scene Editor' />,
            onClick: () => {
                changeStep('3D-product-configurator-scene-editor');
            },
            isPod: false
        },
        {
            key: 'Composer',
            tip: <>The Composer section is where you set the rules on how your product can be customized.</>,
            label: 'Composer',
            icon: <StepIcon src={composerIcon} alt='Composer' />,
            onClick: () => {
                changeStep('3D-product-configurator-composer');
            },
            isPod: false
        },
        {
            key: 'Shopping preview',
            tip: <>The Shopping Preview section shows exactly what your customers see when they enter the configurator at your store.</>,
            label: 'Shopping preview',
            icon: <StepIcon src={shoppingPreviewIcon} alt='Shopping Preview' />,
            onClick: () => {
                changeStep('3D-product-configurator-shopping-preview');
            },
            isPod: false
        }
    ];

    useEffect(() => {
        if (!product?.id && !state.alreadyDoneRedirectToSceneEditor) {
            dispatch(setAlreadyDoneRedirectToSceneEditor(true));
            changeStep('3D-product-configurator-scene-editor');
        } else if (!product?.id && state.alreadyDoneRedirectToSceneEditor) {
            dispatch(setAlreadyDoneRedirectToSceneEditor(false));
            dispatch(modeAction('classic'));
            navigate(backofficeUrl('/Products'));
        }
    }, [product?.id, changeStep]);

    return (
        <Container>
            <ContainerBase>
                <PageHeader
                    title={product?.name || T._('3D Product configurator', 'Admin')}
                    backLabel={T._('Back', 'Admin')}
                    onBackClick={() => {
                        // se il prodotto esiste vado alla lista prod, se non esiste torno al catalogo
                        if (product?.id) {
                            dispatch(modeAction('classic'));
                            navigate(backofficeUrl('/products'));
                        } else {
                            dispatch(modeAction('classic'));
                            changeStep('connect-catalog');
                        }
                    }}
                    isBackVisible={true}
                />
                {product.imageUrl ? <ProductImage src={product.imageUrl} alt='Product Image' /> : <StyledEmptyProductImage />}
                <Section>
                    <SelectionTypeContainer>
                        {configuratorSteps
                            .filter((x) => x)
                            .map((x) => (
                                <SelectionType
                                    disabled={x.key !== 'Scene Editor' && !product.id}
                                    key={x.key}
                                    tip={<>{x.tip}</>}
                                    label={x.label}
                                    icon={x.icon}
                                    onClick={() => x.onClick()}
                                />
                            ))}
                    </SelectionTypeContainer>
                </Section>
                <QuickGuidesSection title={T._('Guides on the above steps', 'Admin')}>
                    <Guides>
                        <li>
                            <Link
                                target='_blank'
                                data-track={'3D Scene Editor'}
                                href={'https://zakeke.zendesk.com/hc/en-us/articles/360009461214-Scene-Editor'}
                            >
                                {T._('3D Scene Editor', 'Admin')}
                            </Link>
                        </li>
                        <li>
                            <Link target='_blank' data-track={'Composer'} href={'https://zakeke.zendesk.com/hc/en-us/sections/12263038000412-Composer'}>
                                {T._('Composer', 'Admin')}
                            </Link>
                        </li>
                        <li>
                            <Link
                                target='_blank'
                                data-track={'Shopping preview'}
                                href={'https://zakeke.zendesk.com/hc/en-us/articles/360009665934-Shopping-Preview'}
                            >
                                {T._('Shopping preview', 'Admin')}
                            </Link>
                        </li>
                    </Guides>
                </QuickGuidesSection>
            </ContainerBase>
        </Container>
    );
};

export default PCStepSelection;
